import { useState, useMemo, useEffect, useRef } from 'react';

import type {
    ExistingRepairBooking,
    NewRepairBooking,
    Product,
    RepairService,
    Shop,
    AppointmentSlot,
    PersonalInfo,
} from '../models/repair-bookings';

const useBookingState = <T extends NewRepairBooking | ExistingRepairBooking>(
    initialBooking: () => T
) => {
    const [booking, setBooking] = useState<T>(initialBooking());
    const initialBookingRef = useRef(initialBooking);

    useEffect(() => {
        initialBookingRef.current = initialBooking;
    }, [initialBooking]);

    const actions = useMemo(() => {
        return {
            reset: () => {
                setBooking(initialBookingRef.current());
            },
            setProduct: (product: Product) => {
                setBooking((prev) => ({ ...prev, product }));
            },
            toggleRepairOption: (service: RepairService) => {
                setBooking((prev) =>
                    'repairOptions' in prev
                        ? {
                              ...prev,
                              repairOptions: prev.repairOptions.some((o) => o.id === service.id)
                                  ? prev.repairOptions.filter((o) => o.id !== service.id)
                                  : [...prev.repairOptions, service],
                          }
                        : prev
                );
            },
            setShop: (shop: Shop) => {
                setBooking((prev) => ({ ...prev, shop }));
            },
            setSelectedSlot: (selectedSlot: AppointmentSlot) => {
                setBooking((prev) => ({ ...prev, selectedSlot }));
            },
            patchBooking: (patch: Partial<T>) => {
                setBooking((prev) => ({ ...prev, ...patch }));
            },
            changePersonalInfo: <K extends keyof PersonalInfo>(
                field: K,
                value: PersonalInfo[K]
            ) => {
                setBooking((prev) =>
                    'personalInfo' in prev
                        ? {
                              ...prev,
                              personalInfo: {
                                  ...prev.personalInfo,
                                  [field]: value,
                              },
                          }
                        : prev
                );
            },
        };
    }, [setBooking]);

    return { booking, actions };
};

export default useBookingState;
